import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, LinearProgress } from '@mui/material';
import clsx from 'clsx';

import WorkerActionsMenu from './WorkerActionsMenu';
import Button from 'modules/views/shared/Button';
import { ReactComponent as ThreeDot } from 'assets/icons/ThreeDot.svg';

import { IOnboardingState } from 'modules/state-mgmt/onboarding/state';
import { OnboardingStatus, WorkerInvitationStatus } from 'models/general';
import { useStyles } from '../styles';
import { StepKey } from 'modules/views/shared/Wizard/config';

interface WorkerCardProps {
  worker: IOnboardingState;
  onEdit: (worker: IOnboardingState) => void;
  onDelete: (worker: IOnboardingState) => void;
  onViewDetails: (worker: IOnboardingState) => void;
  onUploadData: (worker: IOnboardingState) => void;
}

const getWorkerCompletionPercentage = (
  worker: Pick<IOnboardingState, 'id' | 'invitationStatus' | 'signatureUrl' | 'temp' | 'uploadingDetails'>,
): number => {
  const { id, invitationStatus, signatureUrl, temp, uploadingDetails } = worker;
  const { sectionsCompleted, pendingUploads, status, isOffline } = uploadingDetails || {};

  // Offline worker with no upload started yet — nothing is server-confirmed
  if (isOffline && status === OnboardingStatus.PENDING) {
    return 0;
  }

  const isPendingOrProcessingOrOffline =
    status === OnboardingStatus.PENDING || status === OnboardingStatus.PROCESSING || isOffline;

  const consentAcceptancesCount = temp?.consentAcceptances?.length ?? 0;
  let completedItems = 0;

  const countStep = (isComplete: boolean, pendingKey: string) => {
    if (!isComplete) return;
    if (isPendingOrProcessingOrOffline && pendingUploads?.[pendingKey]) return;
    completedItems++;
  };

  // --- 6 Registration sub-steps ---

  countStep(isOffline ? true : !!id, 'workerCreation');

  countStep(
    isOffline
      ? true
      : invitationStatus === WorkerInvitationStatus.ACCEPTED || invitationStatus === WorkerInvitationStatus.NOT_INVITED,
    'workerPasswordCreation',
  );

  // Standard T&C
  countStep(
    isPendingOrProcessingOrOffline ? !!signatureUrl : !!(signatureUrl || consentAcceptancesCount >= 1),
    'signature',
  );

  // Standard Consent Form
  countStep(
    isPendingOrProcessingOrOffline ? !!signatureUrl : !!(signatureUrl || consentAcceptancesCount >= 2),
    'signature',
  );

  // Standard Biometric Consent
  countStep(
    isPendingOrProcessingOrOffline ? !!signatureUrl : !!(signatureUrl || consentAcceptancesCount >= 3),
    'signature',
  );

  // Registration Signature
  countStep(!!signatureUrl, 'signature');

  // --- 6 Post-registration sections ---

  const postRegistrationSections: Array<{ key: StepKey; pendingKey: string }> = [
    { key: StepKey.TAKE_PHOTO, pendingKey: 'profilePicture' },
    { key: StepKey.PERSONAL_INFORMATION, pendingKey: 'personalInformation' },
    { key: StepKey.CONTACT_INFORMATION, pendingKey: 'contactInformation' },
    { key: StepKey.TRADES, pendingKey: 'trades' },
    { key: StepKey.CERTIFICATIONS, pendingKey: 'certifications' },
    { key: StepKey.CONSENT_FORM, pendingKey: 'consentFormData' },
  ];

  for (const { key, pendingKey } of postRegistrationSections) {
    countStep(!!sectionsCompleted?.[key], pendingKey);
  }

  return Math.round((completedItems / 12) * 100);
};

const WorkerCard: React.FC<WorkerCardProps> = ({ worker, onEdit, onDelete, onUploadData, onViewDetails }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === '--') return '--';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getInitial = (name: string) => {
    return name?.charAt(0).toUpperCase();
  };

  const isWorkerPending = worker.uploadingDetails.status === OnboardingStatus.PENDING;
  const isWorkerProcessing = worker.uploadingDetails.status === OnboardingStatus.PROCESSING;
  const isWorkerIncomplete = worker.uploadingDetails.status === OnboardingStatus.INCOMPLETE;
  const isWorkerCompleted = worker.uploadingDetails.status === OnboardingStatus.COMPLETED;

  const workerCardId = worker?.db?.id;

  return (
    <div className={classes.workerCard} data-testid={`worker-card-${workerCardId}`}>
      <div className={classes.workerHeader}>
        <div className={classes.workerHeaderContent}>
          <div className={classes.workerAvatar}>{getInitial(worker?.firstName)}</div>
          <div className={classes.workerInfo}>
            <h3 className={classes.workerName} data-testid={`worker-card-name-${workerCardId}`}>
              {worker?.firstName} {worker?.lastName}
            </h3>
            {/* <p className={classes.workerDate}>{formatDate(worker?.createdAt)}</p> */}
          </div>
        </div>
        {!isWorkerProcessing && (
          <IconButton
            size="small"
            data-testid={`worker-card-menu-button-${workerCardId}`}
            onClick={e => setAnchorEl(e.currentTarget)}
            className={classes.menuButton}
          >
            <ThreeDot />
          </IconButton>
        )}
      </div>

      {!isWorkerCompleted && (
        <div className={classes.progressContainer}>
          <LinearProgress
            variant="determinate"
            value={getWorkerCompletionPercentage(worker) || 0}
            className={classes.progressBar}
            classes={{
              bar: clsx(classes.progressBarFill, {
                [classes.progressBarFillError]: isWorkerIncomplete,
              }),
            }}
          />
          <p
            className={clsx(classes.progressText, {
              [classes.progressTextError]: isWorkerIncomplete,
            })}
            data-testid={`worker-card-progress-${workerCardId}`}
          >
            {getWorkerCompletionPercentage(worker) || 0}%
          </p>
        </div>
      )}

      {isWorkerIncomplete && (
        <div className={classes.errorBar} data-testid={`worker-card-error-bar-${workerCardId}`}>
          <span className={classes.errorBarText}>{t('workerStatus.errors.registrationNotCompleted')}</span>
          <span
            className={classes.errorBarViewDetails}
            data-testid={`worker-view-details-button-${workerCardId}`}
            onClick={() => onViewDetails(worker)}
          >
            {t('workerStatus.actions.viewDetails')}
          </span>
        </div>
      )}

      <div className={classes.workerDetails}>
        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('workerStatus.card.email')}</span>
          <span className={classes.detailValue}>{worker?.email || '--'}</span>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('workerStatus.card.mobileNumber')}</span>
          <span className={classes.detailValue}>{worker?.mobilePhoneNumber || '--'}</span>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('workerStatus.card.projectName')}</span>
          <span className={classes.detailValue}>{worker.temp?.project?.label || '--'}</span>
        </div>

        <div className={classes.workerDates}>
          <div className={classes.dateItem}>
            <span className={classes.detailLabel}>{t('workerStatus.card.createdOn')}</span>
            <span className={classes.detailValue}>{formatDate(worker?.createdAt)}</span>
          </div>
          <div className={classes.dateItem}>
            <span className={classes.detailLabel}>{t('workerStatus.card.lastUpdatedOn')}</span>
            <span className={classes.detailValue}>{formatDate(worker?.lastUpdatedAt)}</span>
          </div>
        </div>
      </div>

      {/* Upload New Button */}
      {(isWorkerPending || isWorkerIncomplete) && (
        <Button
          label={t('workerStatus.actions.uploadData')}
          variant="outlined"
          color="primary"
          testId={`worker-upload-data-button-${workerCardId}`}
          onClick={() => onUploadData(worker)}
          disableRipple
          styleClass={classes.uploadNewButton}
        />
      )}

      <WorkerActionsMenu
        anchorEl={anchorEl}
        onEdit={() => onEdit(worker)}
        onDelete={() => onDelete(worker)}
        onClose={() => setAnchorEl(null)}
      />
    </div>
  );
};

export default WorkerCard;
